<script setup lang="ts">
  import { onMounted } from 'vue';
  import { DOCUMENT_TYPE_ORDER_OPTIONS } from '@/entities/Document';
  import { DocumentSelectionModal } from '@/features/Document';
  import SelectDocumentIcon from '@/shared/assets/icons/SelectDocumentIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashIcon.svg';
  import {
    ColoredButton,
    DefaultTable,
    IconButton,
    PositionUserSelector,
    SelectInput,
    SimpleLoading,
    TableHeader,
  } from '@/shared/ui';
  import { toNumberOrUndefined } from '@/shared/lib';
  import TrainingScheduledDateField from '../TrainingScheduledDateField.vue';
  import TrainingMinistrantesSection from '../TrainingMinistrantesSection.vue';
  import TrainingNotesField from '../TrainingNotesField.vue';
  import type { useSgqPunctualForm } from '../../model/useSgqPunctualForm';

  const props = defineProps<{
    form: ReturnType<typeof useSgqPunctualForm>;
  }>();

  onMounted(async () => {
    await props.form.load();
  });
</script>

<template>
  <div
    v-if="form.loading.value"
    data-testid="sgq-punctual-form-loading"
    class="flex w-full justify-center py-12"
  >
    <SimpleLoading />
  </div>

  <template v-else>
    <div
      data-testid="sgq-punctual-form"
      class="flex flex-col gap-3 border-b border-gray-02 px-6 py-8"
    >
      <span class="text-sm font-semibold text-gray-07">Documento SGQ:</span>

      <div class="max-w-max">
        <ColoredButton
          text="Selecionar Documento"
          :icon="SelectDocumentIcon"
          icon-alt="Selecionar documento"
          data-testid="sgq-punctual-select-document-button"
          @click="form.documentSelection.openModal"
        />
      </div>

      <DefaultTable v-if="form.documentSelection.selectedDocument.value">
        <thead>
          <tr>
            <TableHeader text="Prefixo" />
            <TableHeader text="Título" />
            <TableHeader text="Rev." />
            <TableHeader
              text="Ações"
              th-class="px-6 py-4.5 text-right text-xs font-semibold text-gray-07"
            />
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-02 border-t border-gray-02 bg-white-01">
          <tr data-testid="sgq-punctual-selected-document-row">
            <td class="pl-6 py-4.5 text-sm text-gray-07">
              {{ form.documentSelection.selectedDocument.value.prefix }}
            </td>
            <td class="pl-6 py-4.5 text-sm text-gray-07">
              {{ form.documentSelection.selectedDocument.value.title }}
            </td>
            <td class="pl-6 py-4.5 text-sm text-gray-07">
              {{ form.documentSelection.selectedDocument.value.revision }}
            </td>
            <td class="px-6 py-4.5 text-right text-gray-xx-02">
              <IconButton
                :src="TrashIcon"
                alt="Remover documento"
                data-testid="sgq-punctual-remove-document-button"
                :on-click="form.documentSelection.removeSelectedDocument"
              />
            </td>
          </tr>
        </tbody>
      </DefaultTable>

      <DocumentSelectionModal
        v-if="form.documentSelection.isModalOpen.value"
        selection-mode="single"
        title="Selecionar Documento"
        show-sort
        show-advanced-filter
        require-explicit-close
        :default-amount="10"
        :order-options="DOCUMENT_TYPE_ORDER_OPTIONS"
        :fixed-filters="{ isExternal: 'false' }"
        :initial-selected-revision-id="form.documentSelection.selectedDocument.value?.documentRevisionId ?? null"
        @close="form.documentSelection.closeModal"
        @select="form.documentSelection.handleDocumentSelected"
      />
    </div>

    <div class="flex flex-col gap-3.5 border-b border-gray-02 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Programação do Treinamento</span>
      <div class="grid grid-cols-[304px_348px_348px] gap-4">
        <div class="flex flex-col gap-1.5">
          <span
            id="sgq-punctual-scheduled-by-member-label"
            class="text-sm font-semibold text-gray-07"
            >Responsável pela Programação:</span
          >
          <SelectInput
            :model-value="form.scheduledByMemberId.value ?? ''"
            :options="form.memberOptions.value"
            label="Selecione um colaborador"
            aria-labelledby="sgq-punctual-scheduled-by-member-label"
            @update:model-value="form.setScheduledByMemberId(toNumberOrUndefined($event) ?? null)"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="sgq-punctual-scheduled-by-position-label"
            class="text-sm font-semibold text-gray-07"
            >Cargo:</span
          >
          <SelectInput
            :model-value="form.scheduledByPositionId.value ?? ''"
            :options="form.scheduledByPositions.value"
            label="Selecione o cargo relacionado ao treinamento"
            aria-labelledby="sgq-punctual-scheduled-by-position-label"
            @update:model-value="form.scheduledByPositionId.value = toNumberOrUndefined($event) ?? null"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            id="sgq-punctual-scheduled-date-label"
            class="text-sm font-semibold text-gray-07"
            >Data da programação:</span
          >
          <TrainingScheduledDateField
            :model-value="form.scheduledDate.value"
            testid="sgq-punctual-scheduled-date-trigger"
            labelledby="sgq-punctual-scheduled-date-label"
            @update:model-value="form.scheduledDate.value = $event"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-4 border-b border-gray-02 px-6 py-8">
      <span class="text-sm font-semibold text-gray-07">Colaboradores:</span>
      <PositionUserSelector
        data-testid="sgq-punctual-collaborators-selector"
        :positions="form.collaborators.positions.value"
        :users="form.collaborators.users.value"
        :selected-positions="form.collaborators.selectedPositions.value"
        :selected-users="form.collaborators.selectedUsers.value"
        :positions-selected-count="form.collaborators.positionsSelectedCount.value"
        :users-selected-count="form.collaborators.usersSelectedCount.value"
        @update:selected-positions="form.collaborators.selectedPositions.value = $event"
        @update:selected-users="form.collaborators.selectedUsers.value = $event"
      />
    </div>

    <TrainingMinistrantesSection
      :ministrantes="form.ministrantes"
      :members="form.members.value"
      :member-options="form.memberOptions.value"
    />

    <TrainingNotesField
      :model-value="form.notes.value"
      testid="sgq-punctual-notes-input"
      @update:model-value="form.notes.value = $event"
    />
  </template>
</template>
